import React from "react";
import {
    FormDataConsumer,
    FormTab,
    SimpleFormIterator,
    useRecordContext,
    useResourceContext,
    choices,
    ReferenceInput,
    required,
    useGetOne,
    maxValue,
    regex,
    TextField
} from "react-admin";
import { Typography, Box, Divider, Chip } from '@mui/material';
import { ArrayInput, AutocompleteInput, BooleanInput, NumberInput, RadioButtonGroupInput, RichTextInput, SelectArrayInput, TextInput } from "../../../lib/wrappers/Inputs";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import { regNumber, regString } from "../../../lib/CommonComponents";
import { VersioningTypeInput } from "../../../lib/VersioningTypeInput";

var titleStyle = { color: "#AE223F" }
var labelStyle = {
    color: '#000000',
    opacity: 0.8,
    padding: 0,
    fontsize: 1,
    fontweight: 400, lineheight: 1, letterspacing: 0.00938
}

const maxMarkVal = [required(), maxValue(100)]


export const ModuleAssessmentsInputTab = (props) => {
    const resource = useResourceContext();
    const record = useRecordContext();

    return <FormTab {...props} label="myroot.moduleAssessments">
         <Box display="flex" width={1}>
            <Box flex={4}>
                <Typography variant="h6" style={titleStyle} sx={{ marginBottom: '15px' }}>
                    Module | <small><TextField source="id" label={false} /></small> | <small><TextField source="shortTitle" label={false} /></small>
                </Typography>
            </Box>
        </Box>
        <Box display="flex" width={1}>
            <Box flex={1}>
                <Divider flexItem textAlign="center" sx={{ marginBottom: '15px' }}><b style={labelStyle}><Chip sx={{ fontSize: '1.2em' }} label="Assessment Criteria" /></b></Divider>
            </Box>
        </Box>
        <ArrayInput source="assessmentCriteriaList" label={false}>
        <SimpleFormIterator disableReordering fullWidth>
                <FormDataConsumer>
                    {({ getSource, formData }) =>
                        <Box width={1}>
                            <Box display="flex">
                                <Box flex={4}>
                                    <ReferenceInput reference="Assessment" source={getSource("assessmentId")} label="myroot.assessmentType" fullWidth>
                                        <AutocompleteInput optionText="assessmentType" label="Assessment Type" />
                                    </ReferenceInput>
                                </Box>
                                <Box flex={4} ml="0.5em">
                                    <ReferenceInput reference="Assessment" source={getSource("assessmentId")} label="myroot.assessmentType" fullWidth>
                                        <AutocompleteInput optionText="category" label="Assessment Category" />
                                    </ReferenceInput>
                                </Box>
                                <Box flex={2} ml="0.5em">
                                    <BooleanInput source={getSource("isBestOfAssessment")} label="myroot.isBestOfAssessment" fullWidth sx={{marginLeft:'20px'}}/>
                                </Box>
                            </Box>
                            <Box display="flex">
                                <Box flex={8}>
                                    <TextInput source={getSource("description")} label="myroot.description" fullWidth multiline validate={required()} />
                                </Box>     
                            </Box>
                            <Box display="flex">                               
                                <Box flex={3} >
                                    <NumberInput source={getSource("durationInMin")} label="myroot.durationInMin" min={0} step={1} fullWidth validate={required()}/>                                   
                                </Box>
                                <Box flex={3} ml="0.5em">
                                    <NumberInput source={getSource("wordCount")} label="myroot.wordCount" min={0} step={1} fullWidth validate={regNumber} />                                    
                                </Box>
                                <Box flex={3} ml="0.5em">
                                    <NumberInput source={getSource("weight")} label="myroot.weight" min={0} step={1} fullWidth validate={required()}/>                                    
                                </Box>
                                <Box flex={3} ml="0.5em">
                                    <NumberInput source={getSource("maximumMark")} label="myroot.maximumMark" min={0} step={1} fullWidth validate={maxMarkVal}/>                                    
                                </Box>
                                <Box flex={3} ml="0.5em">
                                    <NumberInput source={getSource("passMark")} label="myroot.passMark" min={0} step={1} fullWidth validate={required()}/>
                                </Box>
                            </Box>
                            <Box display="flex">
                                <Box flex={4}>
                                    {formData.moduleLearningOutcomes ?
                                        <SelectArrayInput source={getSource("learningOutcomeCodes")} label="Assign Learning Outcome" optionValue="Title" optionText="Title" choices={LearningOutcomeChoices(formData.moduleLearningOutcomes)} fullWidth />
                                        : null}
                                </Box>
                            </Box>
                        </Box>
                    }
                </FormDataConsumer>
            </SimpleFormIterator>
        </ArrayInput>
        <Box display="flex" width={1}>
            <Box flex={1}>
                <Divider flexItem textAlign="center" sx={{ marginBottom: '15px' }}><b style={labelStyle}><Chip sx={{ fontSize: '1.2em' }} label="Assessment Bonding" /></b></Divider>
            </Box>
        </Box>
        <FormDataConsumer>
            {({ formData, ...rest }) => <span>
                <Typography variant="body2">
                    {optionLabel(formData.assessmentBonding)}
                </Typography>
            </span>}
        </FormDataConsumer>
        <RadioButtonGroupInput source="assessmentBonding" label="" optionText={optionRenderName} choices={bondingChoices} />
        <FormDataConsumer>
            {({ formData, ...rest }) => formData.assessmentBonding === 'Partially Bonded' &&
                <span>
                    <RichTextInput fullWidth="false" source="assessmentBondingInfo" {...rest} />
                </span>}
        </FormDataConsumer>
        <Box display="flex" width={1}>
            <Box flex={1}>
                <Divider flexItem textAlign="center" sx={{ marginBottom: '15px' }}><b style={labelStyle}><Chip sx={{ fontSize: '1.2em' }} label="Assessment Matrix" /></b></Divider>
            </Box>
        </Box>
        <FormDataConsumer>
            {({ formData, ...rest }) => formData.moduleLearningOutcomes ?
                <AssessmentMatrix {...props} /> : null}
        </FormDataConsumer>

        {resource === "Module" &&
            <VersioningTypeInput source="versioningType" resource={resource} />
        }
    </FormTab>
}



const bondingChoices = [
    { id: 'Bonded', name: 'Bonded', label: 'A student is not required to pass each element of assessment. A student needs only to achieve a pass mark (usually 40%) overall in order to pass the module.' },
    { id: 'Non-Bonded', name: 'Non-Bonded', label: 'A student is required to achieve a pass mark (usually 40%) overall and a minimum of 30% in each element of assessment (as a significant weakness is classed below 30%) in order to pass the module.' },
    { id: 'Partially Bonded', name: 'Partially Bonded', label: 'A student is required to achieve a pass mark (usually 40%) in the following elements of assessment:' },
];
const optionRenderName = choice => `${choice.name}`;
const optionText = ({ choice }) => `${getByValue(bondingChoices, choice).label}`;
const validateValidation = choices(['validated', 'modified', 'corrected'], 'Please check the forms mandatory fields are complete');

const AssessmentMatrix = () => {
    const record = useRecordContext();
    //AssessmentMatrixEdit

    return (
        <div>

            {record ? <div>
                <TableContainer component={Card}>
                    <Table sx={{ minWidth: 650 }} size="large" aria-label="">
                        <TableHead>
                        <TableRow>
                            <TableCell colSpan={record.moduleLearningOutcomes.length + 2}>
                                <Typography variant="h6" style={titleStyle} gutterBottom>
                                    Assessment Matrix
                                </Typography>
                            </TableCell>
                        </TableRow>
                            <TableRow>
                                <TableCell align="center" colSpan={2}>Assessment Information</TableCell>
                                <TableCell  align="center" colSpan={record.moduleLearningOutcomes.length} >Learning Outcomes</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell><b>Category</b></TableCell>
                                <TableCell><b>Type</b></TableCell>
                                {record.moduleLearningOutcomes ?
                                record.moduleLearningOutcomes.map((learningOutcome, index) => {
                                    return (<TableCell key={index} style={{ padding: '.3em .8em', minWidth: '150px' }}  align="center" ><b>{learningOutcome.id}</b></TableCell>)
                                }
                                ) : null}
                                
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {record.assessmentCriteriaList ?
                            record.assessmentCriteriaList.map((criterion, index) => {
                                var row = GetMatrixMatchesForCriterion(criterion, record.moduleLearningOutcomes, index);
                                return row;
                            }) : null}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div> : null}

        </div>
    );

}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

const GetMatrixMatchesForCriterion = (criterion, learningOutcomes, index) => {
    var matches = [];
    const  { data: assessment } = useGetOne('Assessment', { id: criterion.assessmentId});
    if (criterion == null || criterion === undefined)
        return (<TableCell>missing information</TableCell>)

    learningOutcomes?.forEach(learningOutcome => {
        if (criterion.learningOutcomeCodes?.indexOf(learningOutcome.Title) !== -1) {
            matches.push(true);
        } else {
            matches.push(false);
        }
    })
    return <StyledTableRow key={index}>
        
        <StyledTableCell  style={{ padding: '.3em .8em' }}> {assessment?.category}</StyledTableCell >
        <StyledTableCell  style={{ padding: '.3em .8em' }}> {assessment?.assessmentType}</StyledTableCell >
        {matches.map(match => {
            if (match)
                return (<StyledTableCell  align="center">&#10004;</StyledTableCell >)
            else
                return (<StyledTableCell  align="center">-</StyledTableCell >)
        })}</StyledTableRow >
}

const LearningOutcomeChoices = (learningOutcomeArray) => {
    if (Array.isArray(learningOutcomeArray) && learningOutcomeArray.length) {
        var choices = [];
        var i = 0;
        learningOutcomeArray?.forEach(learningOutcome => {
            if (typeof learningOutcome !== 'undefined' && learningOutcome) {
                var choice = { Title: learningOutcome.Title, description: learningOutcome.description, id: i++ }
                choices.push(choice);
            }
        });
        return choices;
    }
    return [{ id: "No data yet", name: "No data yet" }];
}

function optionLabel(val) {
    if (val != undefined) {
        var obj = getByValue(bondingChoices, val);
        return obj.label;
    }
};

function getByValue(arr, value) {
    for (var i = 0, iLen = arr.length; i < iLen; i++) {
        if (arr[i].id == value) {
            return arr[i];
        }
    }
}